import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { SupplierModal } from '../components/SupplierModal';
import { suppliersApi } from '../suppliersApi';
import type { SupplierListItem } from '../supplierTypes';

/**
 * The supplier registry — Procurement › Purchasing › Settings › Suppliers.
 *
 * **THERE IS NO CONTACT-NAME COLUMN, and that is the identity split rather than an omission.**
 * A supplier's contact is a `PersonId` into `rsys_pii`, so a register of thirty rows would be
 * thirty resolves and the list could then be searched and sorted by a value this database does
 * not hold. The PROFILE resolves the one contact on screen.
 */
export function SuppliersIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    row: SupplierListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      suppliersApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          province: query.facets.province?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: SupplierListItem): MenuItem[] => [
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'suppliers.delete',
        disabledReason: row.isActive ? 'This supplier is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await suppliersApi.remove(confirming.row.id);
      else await suppliersApi.restore(confirming.row.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<SupplierListItem>
        module="suppliers"
        breadcrumb={[
          'Procurement',
          'Purchasing',
          { label: 'Settings', to: '/procurement/purchasing/settings' },
          'Suppliers',
        ]}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'province', label: 'Province' },
        ]}
        auditEntityTypes={['Supplier']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by name, code, email, phone or TIN"
        emptyMessage="No supplier yet. Register the ones the organization buys from."
        onCreate={() => setCreating(true)}
        createPermission="suppliers.create"
        onRowOpen={(row) => navigate(`/procurement/purchasing/settings/suppliers/${row.id}`)}
        openPermission="suppliers.read"
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="suppliers.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This supplier is already deleted.')}
        onClearData={() => suppliersApi.clear()}
        clearDataPermission="suppliers.clear-data"
        otherInfo={(row) => row}
      />

      {creating ? (
        <SupplierModal
          open
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete supplier' : 'Restore supplier'}
          danger={confirming.kind === 'delete'}
          // Both safeguards on the destructive half only. A restore puts something back.
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`supplier-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>
                    {confirming.row.name} stops being offered on the order form. Nothing is erased
                    — a deleted supplier can be restored from this list.
                  </p>

                  {/*
                    IT DOES LESS THAN ITS LABEL SUGGESTS, and the count is what makes that
                    honest. Retiring a catalog row stops it being OFFERED and leaves every record
                    already naming it alone — the standing shape here — so a reader expecting the
                    orders to go would otherwise see nothing change.
                  */}
                  {confirming.row.orderCount > 0 ? (
                    <p>
                      The <strong>{confirming.row.orderCount}</strong> purchase order
                      {confirming.row.orderCount === 1 ? '' : 's'} already placed with them keep
                      naming them, and go on resolving to this record.
                    </p>
                  ) : null}

                  {/*
                    THE CONTACT'S NAME IS NOT DESTROYED, which a reader is entitled to know
                    before pressing a destructive button on a record that points into the
                    identity store.
                  */}
                  <p>
                    Their contact person&apos;s name is left in the identity store, so restoring
                    this record finds it still there.
                  </p>
                </>
              ) : (
                <p>{confirming.row.name} returns to the registry and is offered again.</p>
              )}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<SupplierListItem>[] = [
  {
    /*
      THE NAME is the anchor, not the code — a supplier is called "Metro Drug Distribution",
      and a column of `SUP-26001` titles every row identically to anyone who has not memorised
      the sequence. The inventory catalog answers this the other way for the reason its own
      section gives: two live items legitimately share a name, and two live suppliers cannot.
    */
    key: 'name',
    header: 'Supplier',
    width: 280,
    sortable: true,
    render: (row) => <TextCell value={row.name} clamp />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 120,
    sortable: true,
    render: (row) => <TextCell value={row.code} mono />,
  },
  {
    key: 'email',
    header: 'Email',
    width: 220,
    summary: true,
    render: (row) => <TextCell value={row.email} mono clamp />,
  },
  {
    key: 'phone',
    header: 'Phone',
    width: 160,
    summary: true,
    render: (row) => <TextCell value={row.phone} mono />,
  },
  {
    key: 'locationLabel',
    header: 'Address',
    width: 260,
    summary: true,
    render: (row) => <TextCell value={row.locationLabel} clamp />,
  },
  {
    key: 'taxIdentificationNumber',
    header: 'TIN',
    width: 150,
    render: (row) => <TextCell value={row.taxIdentificationNumber} mono />,
  },
  {
    key: 'orderCount',
    header: 'Orders',
    width: 90,
    align: 'right',
    /*
      NOT SORTABLE, deliberately: it is a correlated count, so ordering a page by it makes the
      database compute one per row before it can page — the call the budget department roster
      already takes. The column says so by not offering a header that silently falls back.
    */
    render: (row) => <TextCell value={String(row.orderCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
